import { Audio, cn, Image, Spinner, Stack, Video } from 'component-library';
import { ErrorBoundary } from 'react-error-boundary';

import { SuspenseBoundary } from '@/components';
import { ErrorFallback } from '@/components/widgets/ErrorBoundary';
import { useFileById } from '@/hooks/useFileById';
import { Logger } from '@/lib/logs/logger';

import { MAP_MEDIA_FILE_ALLOWED_FORMATS } from './constants';

type MediaFilePreviewProps = {
  fileInfoId: string | null | undefined;
  className?: string;
  video?: {
    width?: number;
    height?: number;
  };
  audio?: {
    width?: number;
    height?: number;
  };
};

export const MediaFilePreview = ({ fileInfoId, className, video, audio }: MediaFilePreviewProps) => {
  if (!fileInfoId) return null;

  return (
    <ErrorBoundary
      FallbackComponent={({ error }) => <ErrorFallback error={error} />}
      onError={(error, info) => {
        Logger.error(`MediaFilePreview rendering error: ${error} ${info}`);
      }}
    >
      <SuspenseBoundary
        fallback={
          <Stack centerMain>
            <Spinner size={10} />
          </Stack>
        }
      >
        <MediaFileContent fileInfoId={fileInfoId} className={className} video={video} audio={audio} />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const MediaFileContent = ({
  fileInfoId,
  className,
  video,
  audio,
}: {
  fileInfoId: string;
  className?: string;
  video?: { width?: number; height?: number };
  audio?: { width?: number; height?: number };
}) => {
  const { data } = useFileById(fileInfoId);
  const { url, visible_filename: fileName } = data;

  const fileType = fileName.split('.').pop()?.toLowerCase();

  return (
    <MediaFile
      url={url}
      fileName={fileName}
      fileType={fileType ?? ''}
      className={className}
      video={video}
      audio={audio}
    />
  );
};

type LocalFilePreviewProps = {
  file: File;
  className?: string;
  video?: {
    width?: number;
    height?: number;
  };
  audio?: {
    width?: number;
    height?: number;
  };
};

export const LocalFilePreview = ({ file, className, video, audio }: LocalFilePreviewProps) => {
  const url = URL.createObjectURL(file);

  if (!url) {
    return null;
  }

  const fileType = file.name.split('.').pop()?.toLowerCase();

  return (
    <MediaFile
      url={url}
      fileName={file.name}
      fileType={fileType ?? ''}
      className={className}
      video={video}
      audio={audio}
    />
  );
};

const MediaFile = ({
  url,
  fileName,
  fileType,
  className,
  video,
  audio,
}: {
  url: string;
  fileName: string;
  fileType: string;
  className?: string;
  video?: {
    width?: number;
    height?: number;
  };
  audio?: {
    width?: number;
    height?: number;
  };
}) => {
  const mediaType = Object.entries(MAP_MEDIA_FILE_ALLOWED_FORMATS).find(([, formats]) =>
    formats.includes(fileType ?? ''),
  )?.[0];

  const mediaStyles = cn('rounded-xl', className);

  switch (mediaType) {
    case 'IMAGE':
      return (
        <Stack center centerMain>
          <Image src={url} alt={fileName} className={cn(mediaStyles, 'object-contain')} />
        </Stack>
      );
    case 'VIDEO':
      return (
        <Stack center centerMain>
          <Video url={url} className={mediaStyles} width={video?.width} height={video?.height} />
        </Stack>
      );
    case 'AUDIO':
      return (
        <Stack center centerMain>
          <Audio url={url} className={mediaStyles} height={audio?.height} width={audio?.width} />
        </Stack>
      );
    default:
      return null;
  }
};
